import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import TradeHistoryVM from './TradeHistoryVM';
import { observer } from 'mobx-react';
import { PendingTradeRequestsComponent } from './Components/TradeRequests/PendingTradeRequestsComponent';
import { TradeRequestHiddenFields } from './Components/TradeRequests/TradeRequestHiddenFields';
import TradeRequestLookup from '../Models/Trading/Lookups/TradeRequestLookup';
import { AppService, Types } from '../TransactionsTypes';
import { ScreenSize } from 'st-app/Services/AppLayout';
import { CompletedTradesCriteria } from 'st-reporting/Models/TradesCriteria';

@observer
export default class TradeHistoryView extends Views.ViewBase<TradeHistoryVM> {

    constructor(props: unknown) {
        super("Trade History", TradeHistoryVM, props);
    }

    private layout = AppService.get(Types.Shared.Services.AppLayout);

    public render() {
        const hideExpand = this.layout.currentScreenSize >= ScreenSize.Medium;
        const hasCostsAlreadyPaidGroups = this.viewModel.hasCostsAlreadyPaidGroups;
        const metricLabel = hasCostsAlreadyPaidGroups ? "Value" : "Profit";
        const showPriceColumns = this.viewModel.pageManager?.data.some(c => c.showPriceColumns === true) ?? true;
        const showAwardCostsColumn = this.viewModel.pageManager?.data.some(c => c.showAwardCostsColumn === true) ?? true;
        const showUnitColumns = this.viewModel.pageManager?.data.some(c => c.showUnitColumns === true) ?? true;
        const hasPendingTrades = (this.viewModel.tradeRequestVM.portfolioService.appData?.pendingTradeRequests?.length ?? 0) > 0;

        return (
            <div>
                <h2 className="mt-2">My Trade History</h2>

                <div className="mt-5">
                    <PendingTradeRequestsComponent viewModel={this.viewModel.tradeRequestVM} taskRunner={this.taskRunner} />
                </div>

                <div className="mt-5">
                    <div style={{ flex: "auto", display: "flex", alignItems: "flex-end", justifyContent: "flex-end", textAlign: "right" }}>
                        {this.viewModel.portfolioData && this.viewModel.portfolioData.settings.allowParticipantCompletedTradesDownload &&
                            <Neo.Button
                                className="btn-150 mb-3 ms-3 d-none d-sm-inline"
                                onClick={() => this.viewModel.completedTradesCriteria = new CompletedTradesCriteria()}
                                tooltip={"Downloads a PDF statement of the portfolio."}>Download</Neo.Button>}
                    </div>
                    <div className="portfolio-group-container">
                        <div className="portfolio-group-header">
                            <div className="row">
                                <div className="col-xl-4 col-xxl-5">
                                    {hasPendingTrades &&
                                        <div className="portfolio-group-title">
                                            Trade history
                                        </div>}
                                </div>
                                <div className="col-md-6 col-xl-4 col-xxl-3">
                                    <Neo.FormGroup bind={this.viewModel.criteria.meta.incentiveSchemeId} select={{ itemSource: this.viewModel.awardsDataCache.particpantIncentiveSchemes, nullText: "All schemes" }} />
                                </div>
                                <div className="col-6 col-md-3 col-xl-2 col-xxl-2">
                                    <Neo.FormGroup bind={this.viewModel.criteria.meta.fromDate} />
                                </div>
                                <div className="col-6 col-md-3 col-xl-2 col-xxl-2">
                                    <Neo.FormGroup bind={this.viewModel.criteria.meta.toDate} />
                                </div>
                            </div>
                        </div>
                        <div className="portfolio-group-body overflow-x-auto-mobile">
                            <Neo.Pager pageManager={this.viewModel.pageManager} pageControls="none">
                                <NeoGrid.Grid<TradeRequestLookup> collapseAll={hideExpand} className={hideExpand ? "pad-left-cells" : ""}>
                                    {(item, meta) => (
                                        <NeoGrid.RowGroup expandProperty={meta.isExpanded}>
                                            <NeoGrid.Row>
                                                <NeoGrid.Column display={meta.tradeRequestId} label="Ref No.">{item.tradeRequestLinkId && <i className="fa fa-link fa-sm ms-1" data-tip="This is a linked trade."></i>}
                                                </NeoGrid.Column>
                                                <NeoGrid.Column label="Trade Date" display={meta.tradeDate} />
                                                <NeoGrid.Column label="Scheme" display={meta.incentiveSchemeName} className="text-heading" sort={false} />
                                                <NeoGrid.Column display={meta.statusText} className="grid-highlight" />
                                                <NeoGrid.Column label="Trade Instruction" display={meta.type} />
                                                {showUnitColumns &&
                                                    <NeoGrid.Column label="Units" display={item.showUnitColumns ? meta.totalQuantity : meta.emptyPlaceholder} />
                                                }
                                                {showPriceColumns &&
                                                    <NeoGrid.Column label={"Trade\nprice"} display={item.showPriceColumns ? meta.price : meta.emptyPlaceholder} numProps={{ formatString: `${item.currencySymbol} #,##0.00######;(${item.currencySymbol}#,##0.00######)` }} />
                                                }
                                                <NeoGrid.Column label={"Gross\nvalue"} display={meta.grossValue} numProps={{ currencySymbol: item.currencySymbol }} />
                                                {showAwardCostsColumn &&
                                                    <NeoGrid.Column label={"Award\ncosts/income"} display={item.showAwardCostsColumn ? meta.costs : meta.emptyPlaceholder} numProps={{ currencySymbol: item.currencySymbol }} />
                                                }
                                                <NeoGrid.Column label={`${metricLabel}\nbefore tax`} display={meta.profit} numProps={{ currencySymbol: item.currencySymbol }} className="grid-highlight" cellClassName="bold" />
                                                {this.viewModel.showTradeDetailsButton &&
                                                    <NeoGrid.ButtonColumn>
                                                        <Neo.Button size="sm" variant="info" icon="info" onClick={() => this.viewModel.tradeRequestVM.showTradeRequest(item)} />
                                                    </NeoGrid.ButtonColumn>}
                                            </NeoGrid.Row>
                                            <NeoGrid.ChildRow>
                                                {item.totalQuantity && <TradeRequestHiddenFields tradeRequest={item} />}
                                            </NeoGrid.ChildRow>
                                        </NeoGrid.RowGroup>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Pager>

                        </div>
                        <div className="mt-4 d-flex justify-content-end">
                            <Neo.PagerControlsBasic pageManager={this.viewModel.pageManager} />
                        </div>
                    </div>
                </div>


            </div>
        );
    }
}
